import React, { FunctionComponent, useEffect, useState } from 'react'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableBody from '@material-ui/core/TableBody'
import { usePermits } from 'components/views/subscriptions/permits/PermitsProvider'
import { useTranslation } from 'react-i18next'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import IconEdit from '@material-ui/icons/Edit'
import IconStop from '@material-ui/icons/Stop'
import IconDelete from '@material-ui/icons/Delete'
import IconButton from '@material-ui/core/IconButton'
import IconInfo from '@material-ui/icons/InfoOutlined'
import { IPermitsListData, useApi } from 'components/providers/ApiProvider'
import { useHistory } from 'react-router-dom'
import { formatDateTimeFieldToISO, formatTimestamp } from 'utils'
import { useMessage } from 'components/elements/Message'
import EditPermitDialog, { EditPermitDialogData } from 'components/views/subscriptions/permits/partials/EditPermitDialog'
import ListNoRecords from 'Elements/ListNoRecords'
import Tooltip from '@material-ui/core/Tooltip'
import ListLoadingIndicator from 'Elements/ListLoadingIndicator'
import ListSkeleton from 'Elements/ListSkeleton'
import ListTablePagination from 'Elements/ListTablePagination'
import Collapse from '@material-ui/core/Collapse'
import IconExpandMore from '@material-ui/icons/ExpandMore'
import IconExpandLess from '@material-ui/icons/ExpandLess'

const useClasses = makeStyles(theme => ({
  fadeTableRow: {
    '& td': {
      color: theme.palette.grey['A200'],
    },
  },
}))

const NotesTableCell = withStyles(theme => ({
  root: {
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
    maxWidth: 200,
  },
}))(TableCell)

type Props = {}
type Functions = {}
type AllProps = Props & Functions

const List: FunctionComponent<AllProps> = ({}) => {
  const { t } = useTranslation()
  const api = useApi()
  const history = useHistory()
  const { filter, loadList, loadUnexpandedList, listLoading, setListLoading, listPerPage, onChangePerPage, listPage, setListPage, onChangePage } = usePermits()
  const { fadeTableRow } = useClasses()
  const { showSuccess, showError } = useMessage()

  const [modify, setModify] = useState<boolean>(false)
  const [permits, setPermits] = useState<IPermitsListData[]>(null)
  const [totalPermits, setTotalPermits] = useState(0)
  const [expandedUsedPlates, setExpandedUsedPlates] = useState<Set<number>>(new Set())

  const [openEditPermitDialog, setOpenEditPermitDialog] = useState(false)
  const [editPermitDialogData, setEditPermitDialogData] = useState<EditPermitDialogData>(null)


  const handleToggleUsedPlates = (id: number) => {
    setExpandedUsedPlates(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const load = (onLoad?: Function, page?: number) => {
    if (!page) {
      setListLoading(true)
    }

    api.getPermitsList({
      zone_id: filter.zone || undefined,
      plate_number: filter.plateNumber.toUpperCase() || undefined,
      client: filter.client || undefined,
      ...(filter.assignedFrom && { assigned_from: formatDateTimeFieldToISO(filter.assignedFrom) }),
      ...(filter.assignedTill && { assigned_till: formatDateTimeFieldToISO(filter.assignedTill) }),
      notes: filter.notes || undefined,
      limit: listPerPage,
      page: page || listPage,
    })
      .then(data => {
        if (!page && data.permits && !data.permits.length && data.totalPermits) {
          const pages = Math.ceil(data.totalPermits / listPerPage)

          if (listPage > pages) {
            setListPage(pages)
            load(onLoad, pages)

            return
          }
        }

        setListLoading(false)

        if (onLoad) {
          onLoad(data.permits)
        }

        setModify(Boolean(data.modify))
        setPermits(data.permits)
        setTotalPermits(data.totalPermits)
      })
      .catch(error => {
        setListLoading(false)

        if (error.status == 401) {
          history.push('/')

          return
        }

        showError(`${t('alerts.couldNotLoadData')} ${t('alerts.tryLater')}`)
      })
  }

  useEffect(() => {
    load()
  }, [loadList])

  useEffect(() => {
    loadUnexpandedList && load(() => {
      setExpandedUsedPlates(new Set())
    })
  }, [loadUnexpandedList])

  const handleEdit = (permit: IPermitsListData) => {
    const { id, valid_from: validFrom, valid_till: validTill, count, plate_numbers: plateNumbers, notes, modify } = permit

    setEditPermitDialogData({
      id,
      validFrom,
      validTill,
      count,
      plateNumbers,
      notes,
      updatable: modify.updatable,
    })
    setOpenEditPermitDialog(true)
  }

  const handleStop = (permit: IPermitsListData) => {
    if (!confirm(t('confirms.stopPermit'))) {
      return
    }

    api.stopPermit({
      permit_id: permit.id,
    })
      .then(data => {
        if (!data || !data.status) {
          throw new Error
        }

        load()

        showSuccess(t('messages.permitStopped'))
      })
      .catch(error => {
        let message

        switch (error.code) {
          default:
            message = t('alerts.tryLater')
        }

        showError(`${t('alerts.couldNotStopPermit')} ${message}`)
      })
  }

  const handleDelete = (permit: IPermitsListData) => {
    if (!confirm(t('confirms.deletePermit'))) {
      return
    }

    api.deletePermit({
      permit_id: permit.id,
    })
      .then(data => {
        if (!data || !data.status) {
          throw new Error
        }

        load()

        showSuccess(t('messages.permitDeleted'))
      })
      .catch(error => {
        let message

        switch (error.code) {
          default:
            message = t('alerts.tryLater')
        }

        showError(`${t('alerts.couldNotDeletePermit')} ${message}`)
      })
  }

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    onChangePage(page + 1)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChangePerPage(parseInt(event.target.value, 10))
  }

  const nowTime = (new Date).getTime()

  return (
    <>
      {!permits && (
        <ListSkeleton />
      )}

      {permits && listLoading && (
        <ListLoadingIndicator />
      )}

      {permits && !permits.length && (
        <ListNoRecords />
      )}

      {permits && Boolean(permits.length) && (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('zone')}</TableCell>
                  <TableCell>{t('assignedAt')}</TableCell>
                  <TableCell>{t('period')}</TableCell>
                  <TableCell>{t('quantity')}</TableCell>
                  <TableCell>{t('plateNumbers')}</TableCell>
                  <TableCell>{t('client')}</TableCell>
                  <TableCell>{t('notes')}</TableCell>
                  {modify && <TableCell colSpan={4} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {permits.map((permit, key) => {
                  const closedPermit = permit.valid_till && new Date(permit.valid_till).getTime() <= nowTime

                  return (<TableRow key={key} hover {...(closedPermit && { className: fadeTableRow })}>
                    <TableCell>
                      {permit.zone}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {formatTimestamp(permit.assigned_at)}
                    </TableCell>
                    <TableCell>
                      {formatTimestamp(permit.valid_from, 'dd.MM.yyyy')} - {permit.valid_till ? formatTimestamp(permit.valid_till, 'dd.MM.yyyy') : '...'}
                    </TableCell>
                    <TableCell>
                      {permit.count}
                    </TableCell>
                    <TableCell style={{ maxWidth: 400 }}>
                      {!permit.plate_numbers.length && !permit.used_plate_numbers.length && <>{`—`}</>}
                      {!!permit.plate_numbers.length && (
                        <div style={{ fontFamily: '"Roboto Mono", monospace' }}>
                          {Array.from(permit.plate_numbers).sort().join(', ')}
                        </div>
                      )}
                      {!!permit.used_plate_numbers.length && (
                        <div style={{ fontSize: '0.75em' }}>
                          {t('used')}
                          <IconButton
                            size="small"
                            onClick={() => handleToggleUsedPlates(permit.id)}
                            style={{ padding: 0 }}
                          >
                            {expandedUsedPlates.has(permit.id) ? <IconExpandLess fontSize="inherit" /> : <IconExpandMore fontSize="inherit" />}
                          </IconButton>
                          <Collapse in={expandedUsedPlates.has(permit.id)} unmountOnExit>
                            <div style={{ fontFamily: '"Roboto Mono", monospace' }}>
                              {Array.from(permit.used_plate_numbers).sort().join(', ')}
                            </div>
                          </Collapse>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {!permit.clients.length && <>{`—`}</>}
                      {(permit.clients || []).join(', ')}
                    </TableCell>
                    <NotesTableCell>
                      {permit.notes}
                    </NotesTableCell>
                    {modify && (
                      <>
                        <TableCell align="center" style={{ width: 62 }}>
                          <Tooltip title={`#: ${permit.id}`} placement={'top'} arrow interactive>
                            <IconInfo fontSize="small" style={{ verticalAlign: 'middle' }} />
                          </Tooltip>
                        </TableCell>
                        <TableCell style={{ paddingLeft: 0, width: 46 }}>
                          {Boolean(permit.modify.updatable.length) && (
                            <IconButton
                              color="secondary"
                              size="small"
                              style={{ margin: '-5px 0px' }}
                              onClick={() => handleEdit(permit)}
                            >
                              <IconEdit />
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell style={{ paddingLeft: 0, width: 46 }}>
                          {permit.modify.stoppable && (
                            <IconButton
                              color="secondary"
                              size="small"
                              style={{ margin: '-5px 0' }}
                              onClick={() => handleStop(permit)}
                            >
                              <IconStop />
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell style={{ paddingLeft: 0, width: 46 }}>
                          {permit.modify.deletable && (
                            <IconButton
                              color="secondary"
                              size="small"
                              style={{ margin: '-5px 0px' }}
                              onClick={() => handleDelete(permit)}
                            >
                              <IconDelete />
                            </IconButton>
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>)
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <ListTablePagination
            count={totalPermits}
            rowsPerPage={listPerPage}
            page={listPage - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
      {modify && <EditPermitDialog open={openEditPermitDialog} setOpen={setOpenEditPermitDialog} data={editPermitDialogData} />}
    </>
  )
}

export default List
